import { useMemo } from 'react';
import { useMetricConfig } from 'apps/sigma2-ui/src/lib/hooks/useMetricConfig';
import { TableViewOutput } from 'libs/end-to-end/src/custom-viz/TableViewTypes';
import { TableViewCompatibleData } from 'libs/end-to-end/src/custom-viz/TableViewTypes';

interface UseCompatibleTableDataParams {
  data: TableViewOutput;
  selectedGroupBy: string | null;
}

/**
 * Transforms TableViewOutput data into a format compatible with the view mode components.
 * Handles both primary-only data and combined primary+secondary breakdown data.
 */
export const useCompatibleTableData = ({
  data,
  selectedGroupBy,
}: UseCompatibleTableDataParams): TableViewCompatibleData | null => {
  const { getMetricAndFocusAreaFromColumnName } = useMetricConfig();

  const cleanPrimaryRows = useMemo(() => {
    return data.primaryRows.map((row) =>
      row.map((cell, cellIndex) =>
        selectedGroupBy || cellIndex === 0
          ? cell
          : getMetricAndFocusAreaFromColumnName({
                columnName: data.primaryColumnHeaders[cellIndex - 1],
              })?.metric.isMetricBoolean
            ? cell
            : 'N/A',
      ),
    );
  }, [
    data.primaryRows,
    selectedGroupBy,
    getMetricAndFocusAreaFromColumnName,
    data.primaryColumnHeaders,
  ]);

  // Create compatibility layer for existing components
  const compatibleData = useMemo(() => {
    if (!data) return null;

    // If no secondary breakdown, use primary data as-is
    if (!data.secondaryBreakdownData) {
      return {
        columnHeaders: data.primaryColumnHeaders,
        rows: cleanPrimaryRows,
        // This provides a map from the row index to the groupByLevel and secondaryBreakdownLevel.
        rowsMeta: data.primaryRows.map((row) => ({
          groupByLevel: row[0] || '',
          secondaryBreakdownLevel: undefined,
        })),
        sampleSizes: data.sampleSizes,
        booleanColumns: data.booleanColumns,
        confidenceValues: data.confidenceValues,
        impactValues: data.impactValues,
        fullSampleAverage: data.fullSampleAverage,
        selectedGroupBy,
        numeratorSizes: data.numeratorSizes,
      };
    }

    // For bar/pie charts: Combine primary and secondary breakdown data
    const combinedRows = data.secondaryBreakdownData.rows.map((row) => [
      `${row.primaryGroupValue} - ${row.secondaryGroupValue}`, // Combined label
      ...row.metrics, // Metrics remain the same
    ]);

    // This provides a map from the row index to the groupByLevel and secondaryBreakdownLevel.
    const combinedRowsMeta = data.secondaryBreakdownData.rows.map((row) => ({
      groupByLevel: row.primaryGroupValue,
      secondaryBreakdownLevel: row.secondaryGroupValue,
    }));

    return {
      columnHeaders: data.primaryColumnHeaders, // Keep original metric columns
      rows: combinedRows,
      rowsMeta: combinedRowsMeta,
      sampleSizes: data.secondaryBreakdownData.sampleSizes,
      booleanColumns: data.booleanColumns,
      confidenceValues: data.secondaryBreakdownData.confidenceValues,
      impactValues: data.secondaryBreakdownData.impactValues,
      fullSampleAverage: data.secondaryBreakdownData.fullSampleAverage,
      numeratorSizes: data.numeratorSizes,
      selectedGroupBy,
    };
  }, [data, selectedGroupBy, cleanPrimaryRows]);

  return compatibleData;
};
